import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { DataGridPro } from '@mui/x-data-grid-pro';
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonatePlainIconButtonSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateSelectMenuProps,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const RECORD_TYPES = ['SKILL', 'CERTIFICATION'];

const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';
const getUserId = () => localStorage.getItem('user_id') || null;

const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      message = body.error || body.message || message;
    } catch {
      // ignore parse errors
    }
    throw new Error(message);
  }
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

const mapMasterRow = (item) => ({
  id: item.id,
  name: item.name || '',
  recordType: item.recordType || item.record_type || '',
  raw: item,
});

const defaultForm = {
  name: '',
  recordType: 'SKILL',
};

const WorkforceSkillCertificateMaster = () => {
  const { canAdd, canEdit, canDelete } = useFunctionPermissions(FC.SKILLS_CERTIFICATIONS);
  const { toastState, showSuccess, showError, hideToast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectionModel, setSelectionModel] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [mode, setMode] = useState('add');
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [rowPendingDelete, setRowPendingDelete] = useState(null);
  const [focusedField, setFocusedField] = useState('');

  const focusLabelProps = (fieldName, hasValue) => {
    const active = focusedField === fieldName;
    return {
      shrink: hasValue || active,
      sx: hasValue || active ? { bgcolor: ht.white, px: 0.5 } : undefined,
    };
  };

  const fieldFocus = (fieldName) => ({
    onFocus: () => setFocusedField(fieldName),
    onBlur: () => setFocusedField((current) => (current === fieldName ? '' : current)),
  });

  const selectedRow = useMemo(
    () => rows.find((row) => row.id === selectionModel[0]),
    [rows, selectionModel]
  );

  const fetchMasters = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiFetch('conductor/workforce-skill-certificate-masters');
      const mapped = (Array.isArray(data) ? data : []).map(mapMasterRow);
      mapped.sort((a, b) => {
        const typeCmp = a.recordType.localeCompare(b.recordType);
        return typeCmp !== 0 ? typeCmp : a.name.localeCompare(b.name);
      });
      setRows(mapped);
    } catch (err) {
      setRows([]);
      showError(err.message || 'Failed to load skills / certifications');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchMasters();
  }, [fetchMasters]);

  const columns = useMemo(
    () => [
      { field: 'name', headerName: 'Name', flex: 1, minWidth: 220, resizable: true },
      {
        field: 'recordType',
        headerName: 'Record Type',
        width: 160,
        resizable: true,
        renderCell: (params) => ((params.value || '') === '' ? '' : String(params.value).toUpperCase()),
      },
    ],
    []
  );

  const buildPayload = () => {
    const userId = getUserId();
    return {
      name: form.name?.trim(),
      recordType: form.recordType || 'SKILL',
      ...(mode === 'edit' ? { id: editingId } : {}),
      updatedBy: userId,
      ...(mode === 'add' ? { createdBy: userId } : {}),
    };
  };

  const openAdd = () => {
    if (!canAdd) return;
    setMode('add');
    setEditingId(null);
    setForm({ ...defaultForm });
    setFocusedField('');
    setDialogOpen(true);
  };

  const openEdit = () => {
    if (!canEdit || !selectedRow) return;
    setMode('edit');
    setEditingId(selectedRow.id);
    setFocusedField('');
    setForm({
      name: selectedRow.name || '',
      recordType: selectedRow.recordType || 'SKILL',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if ((mode === 'add' && !canAdd) || (mode === 'edit' && !canEdit)) return;
    if (!form.name?.trim()) {
      showError('Name is required');
      return;
    }
    if (!form.recordType) {
      showError('Record type is required');
      return;
    }
    try {
      setSubmitting(true);
      const payload = buildPayload();
      if (mode === 'add') {
        await apiFetch('conductor/workforce-skill-certificate-masters', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        showSuccess('Skill / certification saved successfully');
      } else {
        await apiFetch(`conductor/workforce-skill-certificate-masters/${editingId}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        showSuccess('Skill / certification updated successfully');
      }
      setDialogOpen(false);
      setSelectionModel([]);
      fetchMasters();
    } catch (err) {
      showError(err.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!canDelete || !rowPendingDelete) return;
    try {
      setDeleteLoading(true);
      await apiFetch(`conductor/workforce-skill-certificate-masters/${rowPendingDelete.id}`, {
        method: 'DELETE',
      });
      showSuccess('Record deleted successfully');
      setDeleteOpen(false);
      setRowPendingDelete(null);
      setSelectionModel([]);
      fetchMasters();
    } catch (err) {
      showError(err.message || 'Delete failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box sx={{ ...harmonatePageSx, height: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={harmonatePageTitleSx}>
          Skills / Certifications
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={canEdit ? 'Edit' : 'No edit permission'}>
            <span>
              <IconButton onClick={openEdit} disabled={!selectedRow || !canEdit} sx={harmonatePlainIconButtonSx}>
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={canAdd ? 'Add' : 'No add permission'}>
            <span>
              <IconButton onClick={openAdd} disabled={!canAdd} sx={harmonatePlainIconButtonSx}>
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={canDelete ? 'Delete' : 'No delete permission'}>
            <span>
              <IconButton
                onClick={() => {
                  setRowPendingDelete(selectedRow);
                  setDeleteOpen(true);
                }}
                disabled={!selectedRow || !canDelete}
                sx={harmonatePlainIconButtonSx}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          ...harmonateCardSx,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DataGridPro
          rows={rows}
          columns={columns}
          loading={loading}
          rowHeight={40}
          pagination
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={setSelectionModel}
          sx={{
            ...harmonateDataGridSx,
            flex: 1,
            minHeight: 0,
            '& .MuiDataGrid-row:hover': { bgcolor: ht.secondaryTab, cursor: 'pointer' },
          }}
        />
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>{mode === 'add' ? 'Add Skill / Certification' : 'Edit Skill / Certification'}</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx, overflow: 'visible' }}>
          <TextField
            fullWidth
            size="small"
            required
            label="Name"
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            InputLabelProps={focusLabelProps('name', Boolean(form.name))}
            {...fieldFocus('name')}
            sx={{ mt: 1 }}
          />
          <TextField
            fullWidth
            size="small"
            required
            select
            label="Record Type"
            value={form.recordType}
            onChange={(e) => setForm((prev) => ({ ...prev, recordType: e.target.value }))}
            InputLabelProps={focusLabelProps('recordType', Boolean(form.recordType))}
            {...fieldFocus('recordType')}
            SelectProps={{ MenuProps: harmonateSelectMenuProps }}
          >
            {RECORD_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={submitting} sx={harmonateProceedButtonSx}>
            {submitting ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography sx={{ color: ht.text }}>
            Delete &quot;{rowPendingDelete?.name || 'selected'}&quot; ({rowPendingDelete?.recordType})?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleteLoading} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleDelete} disabled={deleteLoading} sx={harmonateAbortButtonSx}>
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Toast
        open={toastState.open}
        onClose={hideToast}
        message={toastState.message}
        severity={toastState.severity}
        title={toastState.title}
      />
    </Box>
  );
};

export default WorkforceSkillCertificateMaster;
